import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiMic, FiSend, FiVolume2, FiVolumeX, FiRotateCcw, FiUpload, FiSquare } from "react-icons/fi";
import Answer from "./Answer";
import { askChatbot, fetchSpeech, recordingToWav, speechTag } from "../../chatbot/chat";
import { SPEECH_LANGUAGES } from "../../video/subs/languages";

const SUGGESTIONS = [
  "What can you do for me?",
  "Explain what an API is, in simple words",
  "Write a short, polite email asking a client to pay an invoice",
  "آپ کیا کر سکتے ہیں؟",
];
const PROVIDER_NAMES = { llm: "an AI language model", claude: "Claude", local: "a local AI model", free: "a free hosted AI model" };

// The live demonstration: the visitor talks (types or speaks) to the demo chatbot through the very same public API a client gets. Answers stream in as they are written.
export default function ChatWindow({ widgetKey, name, greeting }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [speakOn, setSpeakOn] = useState(false);
  const [listening, setListening] = useState(false);
  const [language, setLanguage] = useState("auto");
  const [provider, setProvider] = useState("");
  const conversation = useRef(null);
  const abort = useRef(null);
  const stopper = useRef(null);
  const player = useRef(null);
  const logRef = useRef(null);
  const fileRef = useRef(null);
  const Recognition = typeof window !== "undefined" ? window.SpeechRecognition || window.webkitSpeechRecognition : null;
  const canRecord = typeof navigator !== "undefined" && !!navigator.mediaDevices && typeof window !== "undefined" && !!window.MediaRecorder;

  useEffect(() => { if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight; }, [messages]);
  useEffect(() => () => { if (abort.current) abort.current.abort(); if (stopper.current) stopper.current(); if (player.current) player.current.pause(); if (window.speechSynthesis) window.speechSynthesis.cancel(); }, []);

  const stopSpeaking = useCallback(() => {
    if (player.current) { player.current.pause(); player.current = null; }
    if (window.speechSynthesis) window.speechSynthesis.cancel();
  }, []);

  const speak = useCallback(async (text) => {
    stopSpeaking();
    const blob = await fetchSpeech(widgetKey, text, language);
    if (blob) {
      const audio = new Audio(URL.createObjectURL(blob));
      player.current = audio;
      audio.play().catch(() => {});
    } else if (window.speechSynthesis && window.SpeechSynthesisUtterance) {
      window.speechSynthesis.speak(new window.SpeechSynthesisUtterance(text.replace(/[*_`#>]+/g, "")));
    }
  }, [widgetKey, language, stopSpeaking]);

  // text = a typed (or recognised) question; audio = a recording the server listens to
  const send = useCallback(async ({ text, audio }) => {
    if (busy) return;
    setBusy(true);
    stopSpeaking();
    const id = Date.now();
    setMessages((m) => [...m, { id, role: "user", text: text || "", pending: !text }, { id: id + 1, role: "assistant", text: "", pending: true }]);
    const patch = (which, change) => setMessages((m) => m.map((x) => (x.id === which ? { ...x, ...change } : x)));
    const controller = new AbortController();
    abort.current = controller;
    let reply = "";
    let failed = false;
    try {
      let body;
      if (audio) {
        body = new FormData();
        body.append("audio", audio, "question.wav");
        body.append("language", language);
        if (conversation.current) body.append("conversation_id", conversation.current);
      } else {
        body = { message: text, conversation_id: conversation.current || undefined, language: language === "auto" ? undefined : language };
      }
      await askChatbot({
        widgetKey, body, signal: controller.signal,
        onEvent: (e) => {
          if (e.conversation_id) conversation.current = e.conversation_id;
          if (e.transcript) patch(id, { text: e.transcript, pending: false });
          if (e.delta) { reply += e.delta; patch(id + 1, { text: reply, pending: false }); }
          if (e.done) { reply = e.reply; patch(id + 1, { text: reply, pending: false }); setProvider(e.provider || ""); }
          if (e.error) { failed = true; patch(id + 1, { text: e.error.message, pending: false, error: true }); if (audio) patch(id, { text: "(voice message)", pending: false }); }
        },
      });
      if (reply && !failed && speakOn) speak(reply);
    } catch (err) {
      if (!err || err.name !== "AbortError") patch(id + 1, { text: "Something went wrong. Please try again.", pending: false, error: true });
    } finally {
      setBusy(false);
    }
  }, [busy, language, widgetKey, speakOn, speak, stopSpeaking]);

  const submit = (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    send({ text });
  };

  const fail = (message) => setMessages((m) => [...m, { id: Date.now(), role: "assistant", text: message, error: true }]);

  const stopListening = () => { if (stopper.current) stopper.current(); };

  const listen = async () => {
    if (busy) return;
    if (listening) { stopListening(); return; }
    if (Recognition) {
      const rec = new Recognition();
      rec.lang = speechTag(language, navigator.language || "en-US");
      rec.interimResults = false;
      rec.maxAlternatives = 1;
      rec.onresult = (e) => { const said = e.results[0][0].transcript; if (said) send({ text: said }); };
      rec.onerror = (e) => { if (e.error === "not-allowed") fail("The microphone is not allowed. Allow it in the browser's address bar and try again."); else if (e.error === "no-speech") fail("No speech was heard. Try again."); else if (e.error !== "aborted") fail("The browser could not recognise speech just now."); };
      rec.onend = () => { setListening(false); stopper.current = null; };
      stopper.current = () => rec.stop();
      setListening(true);
      rec.start();
      return;
    }
    if (!canRecord) { fail("This browser cannot record voice. Use the upload button to send an audio file, or type your question."); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const chunks = [];
      const recorder = new window.MediaRecorder(stream);
      recorder.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setListening(false);
        stopper.current = null;
        try { send({ audio: await recordingToWav(new Blob(chunks, { type: recorder.mimeType })) }); } catch { fail("The recording could not be read."); }
      };
      stopper.current = () => recorder.stop();
      setListening(true);
      recorder.start();
      setTimeout(() => { if (stopper.current === null) return; if (recorder.state === "recording") recorder.stop(); }, 30000);
    } catch {
      fail("The microphone is not available. Allow it in the browser's address bar and try again.");
    }
  };

  const uploadAudio = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file || busy) return;
    let blob = file;
    try { blob = await recordingToWav(file); } catch { /* the server reads wav, mp3, flac and ogg itself */ }
    send({ audio: blob });
  };

  const reset = () => {
    if (abort.current) abort.current.abort();
    stopSpeaking();
    conversation.current = null;
    setMessages([]);
    setProvider("");
    setBusy(false);
  };

  const toggleSpeak = () => { if (speakOn) stopSpeaking(); setSpeakOn((v) => !v); };
  const small = "p-2 rounded-lg border border-navy-border text-gray-300 hover:border-accent-light disabled:opacity-40";

  return (
    <div className="rounded-2xl bg-white/5 border border-navy-border flex flex-col h-[600px] max-h-[85vh]">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-navy-border">
        <div className="min-w-0">
          <div className="font-semibold truncate">{name || "AI Assistant"}</div>
          <div className="text-[11px] text-gray-400">{busy ? "Writing the answer..." : "Ask in any language, by typing or by voice"}</div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <select value={language} onChange={(e) => setLanguage(e.target.value)} aria-label="Language of your voice questions" title="Language you speak (for voice questions)"
            className="bg-navy border border-navy-border rounded-lg text-xs px-2 py-2 text-gray-200 max-w-[130px]">
            {SPEECH_LANGUAGES.map(([code, label]) => <option key={code} value={code}>{code === "auto" ? "Auto language" : label}</option>)}
          </select>
          <button type="button" onClick={toggleSpeak} aria-pressed={speakOn} title={speakOn ? "Answers are read aloud" : "Read the answers aloud"} aria-label="Read answers aloud" className={`${small} ${speakOn ? "bg-accent-light/20 border-accent-light text-accent-light" : ""}`}>
            {speakOn ? <FiVolume2 /> : <FiVolumeX />}
          </button>
          <button type="button" onClick={reset} title="Start a new conversation" aria-label="New conversation" className={small}><FiRotateCcw /></button>
        </div>
      </div>

      <div ref={logRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" role="log" aria-live="polite">
        {greeting && <div className="max-w-[85%] rounded-2xl rounded-bl-sm bg-white/10 px-4 py-2.5 text-sm"><Answer text={greeting} /></div>}
        {!messages.length && (
          <div className="flex flex-wrap gap-2 pt-1">
            {SUGGESTIONS.map((s) => <button key={s} type="button" onClick={() => send({ text: s })} disabled={busy} dir="auto" className="text-xs px-3 py-1.5 rounded-full border border-navy-border text-gray-300 hover:border-accent-light disabled:opacity-40">{s}</button>)}
          </div>
        )}
        {messages.map((m) => (
          <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "rounded-2xl rounded-br-sm bg-brand-gradient text-white" : m.error ? "rounded-2xl rounded-bl-sm bg-red-500/15 border border-red-500/30 text-red-100" : "rounded-2xl rounded-bl-sm bg-white/10"}`}>
              {m.pending && !m.text ? (
                <span className="inline-flex gap-1 items-center text-gray-300" aria-label={m.role === "user" ? "Listening to your recording" : "Thinking"}>
                  {m.role === "user" ? "Listening..." : [0, 1, 2].map((i) => <span key={i} className="w-1.5 h-1.5 rounded-full bg-gray-300 animate-pulse" style={{ animationDelay: `${i * 0.18}s` }} />)}
                </span>
              ) : <Answer text={m.text} />}
            </div>
          </div>
        ))}
      </div>

      <form onSubmit={submit} className="p-3 border-t border-navy-border flex items-end gap-2">
        <textarea value={input} onChange={(e) => setInput(e.target.value)} rows={1} dir="auto" maxLength={4000} aria-label="Your question" placeholder={listening ? "Listening..." : "Type your question..."}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(e); } }}
          className="flex-1 resize-none bg-navy border border-navy-border rounded-xl px-3 py-2.5 text-sm outline-none focus:border-accent-light max-h-28" />
        <input ref={fileRef} type="file" accept="audio/*" onChange={uploadAudio} className="hidden" aria-label="Upload an audio question" />
        <button type="button" onClick={() => fileRef.current && fileRef.current.click()} disabled={busy || listening} title="Ask with an audio file" aria-label="Ask with an audio file" className={`${small} p-2.5`}><FiUpload /></button>
        <button type="button" onClick={listen} disabled={busy && !listening} title={listening ? "Stop and send" : "Ask by voice"} aria-label={listening ? "Stop listening" : "Ask by voice"} aria-pressed={listening}
          className={`p-2.5 rounded-xl border ${listening ? "bg-red-500 border-red-500 text-white animate-pulse" : "border-navy-border text-gray-200 hover:border-accent-light"} disabled:opacity-40`}>
          {listening ? <FiSquare /> : <FiMic />}
        </button>
        <button type="submit" disabled={busy || !input.trim()} aria-label="Send" className="p-2.5 rounded-xl bg-brand-gradient text-white disabled:opacity-40"><FiSend /></button>
      </form>
      <div className="px-4 pb-2.5 text-[11px] text-gray-500">
        {provider ? `Answered by ${PROVIDER_NAMES[provider] || "AI"}. ` : ""}AI answers can be wrong - check important facts.
      </div>
    </div>
  );
}
